<template>
  <div class="flex flex-col w-full h-full gap-3 overflow-y-auto">
    <p class="text-sm text-gray-600">
      בחרו תמונה מתוך ההצעות הקשורות לשירות. התמונה שתבחרו תישמר עבור השירות שלכם.
    </p>

    <!-- Search term (pre-filled with the service name, editable) -->
    <div class="flex items-center gap-2">
      <input-text
        v-model="searchTerm"
        class="flex-1"
        placeholder="חיפוש תמונות..."
        @keyup.enter="search" />
      <Button
        icon="pi pi-search"
        :loading="loading"
        @click="search" />
    </div>

    <!-- Loading skeletons -->
    <div v-if="loading" class="grid grid-cols-3 gap-2">
      <div
        v-for="n in 9"
        :key="n"
        class="aspect-square rounded-lg bg-gray-100 animate-pulse" />
    </div>

    <!-- Results -->
    <div v-else-if="images.length" class="grid grid-cols-3 gap-2">
      <button
        v-for="image in images"
        :key="image.id"
        type="button"
        :disabled="!!savingId"
        class="relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-primary transition-colors disabled:opacity-60"
        @click="selectImage(image)">
        <img :src="image.previewUrl" :alt="searchTerm" class="w-full h-full object-cover" loading="lazy" />
        <div
          v-if="savingId === image.id"
          class="absolute inset-0 flex items-center justify-center bg-black/40">
          <i class="pi pi-spinner pi-spin text-white text-xl" />
        </div>
      </button>
    </div>

    <!-- Empty state -->
    <div v-else class="flex flex-col items-center justify-center text-center text-gray-400 py-10 gap-2">
      <i class="pi pi-image text-3xl" />
      <span class="text-sm">לא נמצאו תמונות מתאימות. נסו מונח חיפוש אחר.</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ISuggestedImage } from '@torim-app/types';
import { onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { ServicesApi } from '@app/api/admin/services-api';

const toast = useToast();

const emit = defineEmits(['close', 'select']);
const props = defineProps<{
  serviceName?: string;
}>();

const searchTerm = ref<string>(props.serviceName?.trim() ?? '');
const images = ref<ISuggestedImage[]>([]);
const loading = ref<boolean>(false);
const savingId = ref<string | null>(null);

const search = async () => {
  const term = searchTerm.value.trim();
  if (!term) return;
  try {
    loading.value = true;
    images.value = await ServicesApi.getSuggestedImages(term);
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לטעון תמונות מוצעות, אנא נסו שוב');
    images.value = [];
  } finally {
    loading.value = false;
  }
};

const selectImage = async (image: ISuggestedImage) => {
  if (savingId.value) return;
  try {
    savingId.value = image.id;
    const imageUrl = await ServicesApi.saveSuggestedImage(image.sourceUrl);
    emit('select', imageUrl);
    emit('close');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את התמונה, אנא נסו שוב');
  } finally {
    savingId.value = null;
  }
};

onMounted(() => {
  if (searchTerm.value) search();
});
</script>
